import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faRobot,
  faPaperPlane,
  faTimes,
  faTag,
  faPlane,
  faLightbulb,
  faBullhorn,
  faFolder,
  faChevronUp,
  faChevronDown,
  faUser,
  faBell,
  faComment,
  faCloudUploadAlt,
  faFilePdf,
  faFileExcel,
  faEllipsisV,
  faFilePdf as faFilePdfIcon,
  faSun,
  faMoon
} from "@fortawesome/free-solid-svg-icons";

// Icon mapping
const faIconsMap = {
  faTag,
  faPlane,
  faLightbulb,
  faBullhorn,
  faFolder,
  faUser,
  faPaperPlane,
  faRobot,
  faUserCircle,
  faBell,
  faComment,
  faCloudUploadAlt,
  faFilePdf,
  faFileExcel,
  faEllipsisV,
  faChevronUp,
  faChevronDown,
  faSun,
  faMoon,
};

export default function AccountantDashboardSectionCustomComponents1() {
  // Demo data
  const expenseCategories = [
    { label: "Office Supplies", icon: "faTag" },
    { label: "Travel", icon: "faPlane" },
    { label: "Utilities", icon: "faLightbulb" },
    { label: "Advertising", icon: "faBullhorn" }
  ];
  const businessClients = [
    {
      name: "Client A Corp",
      taxYears: [
        { year: "2023", expenses: ["Office Supplies", "Travel", "Utilities", "Advertising"] },
        { year: "2022", expenses: ["Office Supplies", "Travel"] }
      ]
    },
    {
      name: "Client B LLC",
      taxYears: [
        { year: "2023", expenses: ["Office Supplies", "Utilities"] }
      ]
    }
  ];
  const individualClients = [
    {
      name: "John Smith",
      taxYears: [
        { year: "2023", expenses: ["Office Supplies", "Travel"] }
      ]
    },
    {
      name: "Jane Doe",
      taxYears: [
        { year: "2022", expenses: ["Advertising"] }
      ]
    }
  ];
  const taxYears = ["2023", "2022", "2021"];

const [categorySummary, setCategorySummary] = useState(null);

useEffect(() => {
  async function fetchSummary() {
    const token = localStorage.getItem("token");
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000"}/expenses/summary`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (res.ok) {
      const data = await res.json();
      if (data.categories) {
        setCategorySummary(
          Object.entries(data.categories).map(([category, info]) => ({
            category,
            total: info.total,
            documents: info.items,
          }))
        );
      } else {
        setCategorySummary([]); // Fallback to empty array
      }
    } else {
      setCategorySummary([]); // Also fallback on fetch error
    }
  }
  fetchSummary();
}, []);

  // Sidebar state
  const [filterOpen, setFilterOpen] = useState(true);
  const [customersOpen, setCustomersOpen] = useState(true);
  const [businessOpen, setBusinessOpen] = useState(true);
  const [individualsOpen, setIndividualsOpen] = useState(true);
  const [openCustomers, setOpenCustomers] = useState({});
  const [openTaxYears, setOpenTaxYears] = useState({});

  const toggleCustomer = (name) => {
    setOpenCustomers((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };
  const toggleTaxYear = (customerName, year) => {
    const key = `${customerName}_${year}`;
    setOpenTaxYears((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // DARK MODE STATE
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== "undefined") {
      return (
        window.localStorage.getItem("theme") === "dark" ||
        (!window.localStorage.getItem("theme") &&
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      );
    }
    return false;
  });

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark");
      window.localStorage.setItem("theme", "dark");
    } else {
      document.body.classList.remove("dark");
      window.localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  // ==== AI Chat State ====
  const [aiOpen, setAiOpen] = useState(false);
  const [messages, setMessages] = useState([
    { text: "Hello! How can I assist you today?", fromUser: false }
  ]);
  const aiContainerRef = useRef(null);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [aiFiles, setAiFiles] = useState([]);

function handleAiFileChange(e) {
  setAiFiles(Array.from(e.target.files));
}

function clearAiFiles() {
  setAiFiles([]);
}

async function handleAiSend(e) {
  e.preventDefault();
  if (!aiInput.trim() && aiFiles.length === 0) return;
  
  // Add user message to chat immediately if there's text input
  if (aiInput && aiInput.trim()) {
    setMessages(prev => [
      ...prev,
      { text: aiInput.trim(), fromUser: true }
    ]);
  }
  
  setAiLoading(true);

  const token = localStorage.getItem("token");
  let formData = new FormData();

  if (aiInput && aiInput.trim()) formData.append("question", aiInput.trim());
  if (aiFiles && aiFiles.length > 0) {
    aiFiles.forEach(f => formData.append("files", f));
  }

  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000"}/ai/ask`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData,
      }
    );
    const data = await res.json();
    
    console.log("API Response:", data); // Debug log
    
    if (res.ok) {
      // Handle file upload message
      if (data.upload_message) {
        setMessages(prev => [
          ...prev,
          { text: data.upload_message, fromUser: false }
        ]);
      }
      
      // Handle AI response
      if (data.answer) {
        setMessages(prev => [
          ...prev,
          { text: data.answer, fromUser: false }
        ]);
      }
      
      // Handle enhanced analysis data (optional display)
      if (data.document_analyses && data.document_analyses.length > 0) {
        const analysisCount = data.document_analyses.length;
        const successCount = data.document_analyses.filter(doc => doc.processing_status === "success").length;
        
        setMessages(prev => [
          ...prev,
          { text: `📊 Analyzed ${successCount}/${analysisCount} documents successfully`, fromUser: false }
        ]);
        
        // Show summary if available
        if (data.summary && data.summary.total_potential_deductions > 0) {
          setMessages(prev => [
            ...prev,
            { text: `💰 Found $${data.summary.total_potential_deductions.toFixed(2)} in potential deductions`, fromUser: false }
          ]);
        }
      }
      
      // Handle errors
      if (data.ai_error) {
        setMessages(prev => [
          ...prev,
          { text: `❌ ${data.ai_error}`, fromUser: false }
        ]);
      }
      
      // If no response content was found
      if (!data.answer && !data.upload_message && !data.ai_error) {
        setMessages(prev => [
          ...prev,
          { text: "I received your request but couldn't generate a response. Please try again.", fromUser: false }
        ]);
      }
    } else {
      setMessages(prev => [
        ...prev,
        { text: data.detail || "Sorry, something went wrong.", fromUser: false }
      ]);
    }
  } catch (error) {
    console.error("Network error:", error); // Debug log
    setMessages(prev => [
      ...prev,
      { text: "Network error. Please try again.", fromUser: false }
    ]);
  }
  
  setAiInput("");
  setAiLoading(false);
  setAiFiles([]);
}

  // ==== Generate Report Modal State ====
  const [reportFormOpen, setReportFormOpen] = useState(false);
  const [reportType, setReportType] = useState("Summary");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedYear, setSelectedYear] = useState(taxYears[0] || "");

  // ==== Multi-file upload with S3 integration ====
  const [uploadSelectedFiles, setUploadSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const uploadInputRef = useRef(null);

  // Fetch recent uploads from backend
  const fetchRecentUploads = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000"}/uploads/recent`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.ok) {
        const files = await res.json();
        setUploadedFiles(
          files.map((f) => ({
            name: f.original_filename || f.s3_key || "Document",
            type: (f.s3_key || "").endsWith(".pdf")
              ? "pdf"
              : (f.s3_key || "").endsWith(".xls") ||
                (f.s3_key || "").endsWith(".xlsx") ||
                (f.s3_key || "").endsWith(".csv")
              ? "excel"
              : "file",
            size: (f.size ? `${(f.size / 1024).toFixed(1)} KB` : ""),
            date: f.uploaded_at
              ? new Date(f.uploaded_at).toLocaleString()
              : "",
          }))
        );
      }
    } catch (err) {
      // eslint-disable-next-line
      console.error("Failed to fetch uploads", err);
    }
  };

  useEffect(() => {
    fetchRecentUploads();
  }, []);

  function handleFileChange(e) {
    let files = Array.from(e.target.files);
    setUploadSelectedFiles((prev) => [...prev, ...files]);
  }
  function handleDrop(e) {
    e.preventDefault();
    let files = Array.from(e.dataTransfer.files);
    setUploadSelectedFiles((prev) => [...prev, ...files]);
  }
  function handleDragOver(e) {
    e.preventDefault();
  }
  function removeSelectedFile(index) {
    setUploadSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }

  // S3 UPLOAD LOGIC (multi-file, silent, with auth)
  async function handleUpload() {
    if (uploadSelectedFiles.length === 0) return;
    setUploading(true);
    setUploadStatus("Uploading...");
    const token = localStorage.getItem("token");
    const orgName = "Acme CPA"; // Optionally dynamic
    const taxYear = "2023"; // Optionally dynamic
    let errors = [];
    for (const file of uploadSelectedFiles) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("org_name", orgName);
      formData.append("tax_year", taxYear);

      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000"}/upload/s3`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );
        if (!res.ok) {
          errors.push(file.name);
        }
      } catch (err) {
        errors.push(file.name);
      }
    }
    setUploading(false);
    setUploadSelectedFiles([]);
    setUploadStatus(
      errors.length === 0
        ? "Upload successful!"
        : `Some files failed: ${errors.join(", ")}`
    );
    fetchRecentUploads(); // Refresh recent uploads
    setTimeout(() => setUploadStatus(""), 3000);
  }

  return (
    <React.Fragment>
      <div data-from-ai="false" className="bs-section-dragged">
        <div className={`flex h-screen ${darkMode ? "bg-gray-900" : "bg-gray-100"}`}>
          {/* Sidebar */}
          <div className={`w-64 h-full overflow-y-auto shadow-lg ${darkMode ? "bg-gray-800" : "bg-white"}`}>
            <div className="p-4 border-b dark:border-gray-700">
              <h1 className={`text-xl font-bold ${darkMode ? "text-gray-100" : "text-gray-800"}`}>CPA Dashboard</h1>
            </div>
            {/* Customer Filter section - collapsible */}
            <div className="px-4 py-2">
              <div className="p-4 border-b dark:border-gray-700">
                <div className="flex justify-between items-center mb-1">
                  <h2 className={`text-md font-bold ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Customer Filter</h2>
                  <button
                    onClick={() => setFilterOpen((open) => !open)}
                    className="focus:outline-none"
                    aria-label={filterOpen ? "Collapse" : "Expand"}
                  >
                    <FontAwesomeIcon icon={filterOpen ? faChevronUp : faChevronDown} className={darkMode ? "text-gray-300" : "text-gray-600"} />
                  </button>
                </div>
                {filterOpen && (
                  <div className="space-y-3 mt-2">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>Customer Type</label>
                      <select className={`w-full px-2 py-1.5 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                        ${darkMode ? "bg-gray-900 text-gray-200 border-gray-700" : "bg-white text-gray-800 border-gray-300"}`}>
                        <option>All Customers</option>
                        <option>Business</option>
                        <option>Individual</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>Search</label>
                      <input
                        type="text"
                        placeholder="Search customers..."
                        className={`w-full px-2 py-1.5 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                          ${darkMode ? "bg-gray-900 text-gray-200 border-gray-700" : "bg-white text-gray-800 border-gray-300"}`}
                      />
                    </div>
                    <div className="pt-2">
                      <button className="w-full px-3 py-1.5 text-xs text-center font-medium bg-blue-500 hover:bg-blue-600 focus:ring-2 focus:ring-blue-200 rounded-md text-white">
                        Apply Filters
                      </button>
                      <button className="w-full px-3 py-1.5 mt-2 text-xs text-center font-medium rounded-md
                        bg-gray-200 hover:bg-gray-300 text-gray-700
                        dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300
                        focus:ring-2 focus:ring-gray-100">
                        Reset
                      </button>
                    </div>
                  </div>
                )}
              </div>
                          {/* Customers Section (collapsible, with Business/Individuals) */}
                          <div className="mb-4">
                            <div
                              className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                              onClick={() => setCustomersOpen((open) => !open)}
                            >
                              <div className="flex items-center">
                                <FontAwesomeIcon icon={faFolder} className="mr-2 text-blue-600" />
                                <span className={`font-medium ${darkMode ? "text-gray-100" : "text-gray-800"}`}>Customers</span>
                              </div>
                              <FontAwesomeIcon icon={customersOpen ? faChevronUp : faChevronDown} className="text-gray-500" />
                            </div>
                            {customersOpen && (
                              <div className="ml-2 mt-1">
                                {/* Business subfolder */}
                                <div className="cursor-pointer">
                                  <div
                                    className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                                    onClick={() => setBusinessOpen((open) => !open)}
                                  >
                                    <div className="flex items-center">
                                      <FontAwesomeIcon icon={faFolder} className="mr-2 text-blue-500" />
                                      <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>Business</span>
                                    </div>
                                    <FontAwesomeIcon icon={businessOpen ? faChevronUp : faChevronDown} className="text-gray-500" />
                                  </div>
                                  {businessOpen && (
                                    <div className="ml-4">
                                      {businessClients.map((client) => (
                                        <div key={client.name}>
                                          <div
                                            className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                            onClick={() => toggleCustomer(client.name)}
                                          >
                                            <div className="flex items-center">
                                              <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                                              <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>{client.name}</span>
                                            </div>
                                            <FontAwesomeIcon icon={openCustomers[client.name] ? faChevronUp : faChevronDown} className="text-gray-500" />
                                          </div>
                                          {openCustomers[client.name] && (
                                            <div className="ml-4">
                                              {client.taxYears.map((taxYear) => {
                                                const yearKey = `${client.name}_${taxYear.year}`;
                                                return (
                                                  <div key={taxYear.year}>
                                                    <div
                                                      className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                                      onClick={() => toggleTaxYear(client.name, taxYear.year)}
                                                    >
                                                      <div className="flex items-center">
                                                        <FontAwesomeIcon icon={faFolder} className="mr-2 text-green-500" />
                                                        <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>{taxYear.year}</span>
                                                      </div>
                                                      <FontAwesomeIcon icon={openTaxYears[yearKey] ? faChevronUp : faChevronDown} className="text-gray-500" />
                                                    </div>
                                                    {openTaxYears[yearKey] && (
                                                      <div className="ml-6">
                                                        {expenseCategories
                                                          .filter(cat => taxYear.expenses.includes(cat.label))
                                                          .map(cat => (
                                                            <div key={cat.label} className={`flex items-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800`}>
                                                              <FontAwesomeIcon icon={faIconsMap[cat.icon]} className="mr-2 text-gray-500" />
                                                              <span className={`text-xs ${darkMode ? "text-gray-200" : "text-gray-700"}`}>{cat.label}</span>
                                                            </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {/* Individuals subfolder */}
                                <div className="cursor-pointer">
                                  <div
                                    className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 mt-2"
                                    onClick={() => setIndividualsOpen((open) => !open)}
                                  >
                                    <div className="flex items-center">
                                      <FontAwesomeIcon icon={faFolder} className="mr-2 text-blue-500" />
                                      <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>Individuals</span>
                                    </div>
                                    <FontAwesomeIcon icon={individualsOpen ? faChevronUp : faChevronDown} className="text-gray-500" />
                                  </div>
                                  {individualsOpen && (
                                    <div className="ml-4">
                                      {individualClients.map((client) => (
                                        <div key={client.name}>
                                          <div
                                            className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                            onClick={() => toggleCustomer(client.name)}
                                          >
                                            <div className="flex items-center">
                                              <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-500" />
                                              <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>{client.name}</span>
                                            </div>
                                            <FontAwesomeIcon icon={openCustomers[client.name] ? faChevronUp : faChevronDown} className="text-gray-500" />
                                          </div>
                                          {openCustomers[client.name] && (
                                            <div className="ml-4">
                                              {client.taxYears.map((taxYear) => {
                                                const yearKey = `${client.name}_${taxYear.year}`;
                                                return (
                                                  <div key={taxYear.year}>
                                                    <div
                                                      className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                                      onClick={() => toggleTaxYear(client.name, taxYear.year)}
                                                    >
                                                      <div className="flex items-center">
                                                        <FontAwesomeIcon icon={faFolder} className="mr-2 text-green-500" />
                                                        <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>{taxYear.year}</span>
                                                      </div>
                                                      <FontAwesomeIcon icon={openTaxYears[yearKey] ? faChevronUp : faChevronDown} className="text-gray-500" />
                                                    </div>
                                                    {openTaxYears[yearKey] && (
                                                      <div className="ml-6">
                                                        {expenseCategories
                                                          .filter(cat => taxYear.expenses.includes(cat.label))
                                                          .map(cat => (
                                                            <div key={cat.label} className={`flex items-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800`}>
                                                              <FontAwesomeIcon icon={faIconsMap[cat.icon]} className="mr-2 text-gray-500" />
                                                              <span className={`text-xs ${darkMode ? "text-gray-200" : "text-gray-700"}`}>{cat.label}</span>
                                                            </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
              {/* Category Summary */}
{categorySummary === null && (
  <div className="mt-6 text-xs text-gray-400">Loading summary…</div>
)}
{Array.isArray(categorySummary) && categorySummary.length > 0 && (
  <div className="mt-6 mb-6">
    <h3 className="text-xs font-bold mb-2 text-gray-700 dark:text-gray-200">
      Category Totals
    </h3>
    <ul>
      {categorySummary.map(cat => (
        <li key={cat.category} className="mb-2">
          <div className="flex items-center justify-between">
            <span>
              <FontAwesomeIcon icon={faTag} className="mr-2 text-blue-500" />
              <span className="font-semibold">{cat.category}</span>
            </span>
            <span className="font-bold">${cat.total.toFixed(2)}</span>
          </div>
          <ul className="ml-6 mt-1 text-xs text-gray-500 dark:text-gray-400">
             {(Array.isArray(cat.documents) ? cat.documents : []).map((doc, idx)  => (
              <li key={doc.source + idx}>
                {doc.source} — ${doc.amount.toFixed(2)}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  </div>
)}
{Array.isArray(categorySummary) && categorySummary.length === 0 && (
  <div className="mt-6 text-xs text-gray-400">No summary data available.</div>
)}

            </div>
          </div>
          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Top Navigation */}
            <header className={`shadow-sm p-4 flex items-center justify-between ${darkMode ? "bg-gray-800" : "bg-white"}`}>
              <h1 className={`text-xl font-bold ${darkMode ? "text-gray-100" : "text-gray-800"}`}>Overview</h1>
              <div className="flex items-center space-x-4">
                {/* Dark/Light Mode Toggle (to the left of notifications) */}
                <button
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                  onClick={() => setDarkMode((prev) => !prev)}
                  aria-label="Toggle Dark Mode"
                  title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                >
                  <FontAwesomeIcon
                    icon={darkMode ? faSun : faMoon}
                    className={darkMode ? "text-yellow-300" : "text-gray-700"}
                  />
                </button>
                {/* Notification */}
                <div className="relative">
                  <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 relative">
                    <FontAwesomeIcon icon={faBell} className={darkMode ? "text-gray-200" : "text-gray-600"} />
                    <span className="absolute top-0 right-0 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-xs text-white">3</span>
                  </button>
                </div>
                {/* Messages */}
                <div className="relative">
                  <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 relative">
                    <FontAwesomeIcon icon={faComment} className={darkMode ? "text-gray-200" : "text-gray-600"} />
                    <span className="absolute top-0 right-0 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-xs text-white">5</span>
                  </button>
                </div>
                {/* User profile - at top right */}
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full border-2 ${darkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-gray-100"} flex items-center justify-center`}>
                    <FontAwesomeIcon icon={faUser} className={`${darkMode ? "text-gray-200" : "text-gray-500"} text-xl`} />
                  </div>
                  <div className="ml-3 text-right">
                    <p className={`text-sm font-medium ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Jessica Chen, CPA</p>
                    <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Senior Accountant</p>
                  </div>
                </div>
              </div>
            </header>
            {/* Main Content Area */}
            <main className={`flex-1 overflow-y-auto p-4 ${darkMode ? "bg-gray-900" : "bg-gray-50"}`}>
              {/* Document Upload Section (Multi-file) */}
              <div className={`rounded-lg shadow-md p-6 mb-6 ${darkMode ? "bg-gray-800" : "bg-white"}`}>
                <h2 className={`text-lg font-bold mb-4 ${darkMode ? "text-gray-100" : "text-gray-800"}`}>Document Upload</h2>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-150 cursor-pointer ${darkMode ? "border-gray-600 bg-gray-900 hover:bg-gray-800" : "border-gray-300 bg-gray-50 hover:bg-gray-100"}`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => uploadInputRef.current.click()}
                >
                  <FontAwesomeIcon icon={faCloudUploadAlt} className={`text-4xl mb-3 ${darkMode ? "text-gray-500" : "text-gray-400"}`} />
                  <p className={`mb-2 ${darkMode ? "text-gray-300" : "text-gray-600"}`}>Drag and drop files here or</p>
                  <button
                    type="button"
                    className="inline-block px-6 py-2.5 text-sm text-center text-white font-bold bg-blue-500 hover:bg-blue-600 focus:ring-4 focus:ring-blue-200 rounded-full"
                    onClick={e => {
                      e.stopPropagation();
                      uploadInputRef.current.click();
                    }}
                  >
                    Browse Files
                  </button>
                  <input
                    type="file"
                    ref={uploadInputRef}
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  {uploadSelectedFiles.length > 0 && (
                    <div className="mt-5 text-left">
                      <p className={`font-medium mb-2 ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Selected Files:</p>
                      <ul>
                        {uploadSelectedFiles.map((file, idx) => (
                          <li key={idx} className="flex items-center mb-2">
                            <FontAwesomeIcon
                              icon={file.type.includes("pdf") ? faFilePdf : faFileExcel}
                              className={file.type.includes("pdf") ? "text-red-500 mr-2" : "text-green-500 mr-2"}
                            />
                            <span className={`mr-3 ${darkMode ? "text-gray-100" : "text-gray-800"}`}>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                            <button
                              className="ml-2 px-2 py-1 text-xs text-white bg-red-500 hover:bg-red-600 rounded"
                              onClick={e => {
                                e.stopPropagation();
                                removeSelectedFile(idx);
                              }}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                      <button
                        className="mt-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-full"
                        disabled={uploading}
                        onClick={e => {
                          e.stopPropagation();
                          handleUpload();
                        }}
                      >
                        {uploading ? "Uploading..." : "Upload Files"}
                      </button>
                      {uploadStatus && (
                        <div className="mt-2 text-sm text-blue-600">{uploadStatus}</div>
                      )}
                    </div>
                  )}
                  <p className={`text-xs mt-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Supported formats: PDF, XLSX, CSV, JPG (Max size: 20MB)</p>
                </div>
                <div className="mt-4">
                  <h3 className={`font-medium mb-2 ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Recent Uploads</h3>
                  <div className="space-y-2">
                    {uploadedFiles.length === 0 && <div className="text-sm text-gray-400">No uploads yet.</div>}
                    {uploadedFiles.map((file, idx) => (
                      <div key={file.name + idx} className={`flex items-center justify-between p-3 rounded-md ${darkMode ? "bg-gray-900" : "bg-gray-50"}`}>
                        <div className="flex items-center">
                          <FontAwesomeIcon
                            icon={file.type === "pdf" ? faFilePdf : faFileExcel}
                            className={file.type === "pdf" ? "text-red-500 mr-3" : "text-green-500 mr-3"}
                          />
                          <span className={`${darkMode ? "text-gray-100" : "text-gray-800"}`}>{file.name}</span>
                        </div>
                        <div className="flex items-center">
                          <span className={`text-xs mr-3 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{file.size} {file.date && `• ${file.date}`}</span>
                          <button className={`${darkMode ? "text-gray-600 hover:text-gray-400" : "text-gray-400 hover:text-gray-600"}`}>
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Chart Generation Section */}
              <div className={`rounded-lg shadow-md p-6 mb-6 ${darkMode ? "bg-gray-800" : "bg-white"}`}>
                <h2 className={`text-lg font-bold mb-4 ${darkMode ? "text-gray-100" : "text-gray-800"}`}>Chart Generation</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className={`p-4 border rounded-lg ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                      <h3 className={`font-medium mb-2 ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Revenue by Quarter</h3>
                      <img src="https://placehold.co/400x200" alt="Revenue Chart" className="w-full h-auto" />
                      <div className="mt-3 flex justify-end">
                        <button className="inline-block px-4 py-2 text-xs text-center text-white font-bold bg-blue-500 hover:bg-blue-600 focus:ring-4 focus:ring-blue-200 rounded-full">Export</button>
                        <button className="inline-block px-4 py-2 ml-2 text-xs text-center font-bold bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-4 focus:ring-blue-200 rounded-full">Edit</button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className={`p-4 border rounded-lg ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                      <h3 className={`font-medium mb-2 ${darkMode ? "text-gray-200" : "text-gray-700"}`}>Expense Distribution</h3>
                      <img src="https://placehold.co/400x200" alt="Expense Chart" className="w-full h-auto" />
                      <div className="mt-3 flex justify-end">
                        <button className="inline-block px-4 py-2 text-xs text-center text-white font-bold bg-blue-500 hover:bg-blue-600 focus:ring-4 focus:ring-blue-200 rounded-full">Export</button>
                        <button className="inline-block px-4 py-2 ml-2 text-xs text-center font-bold bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-4 focus:ring-blue-200 rounded-full">Edit</button>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Generate Report Button */}
                <button
                  className="inline-block px-8 py-3 mb-4 text-sm text-center text-white font-bold bg-green-600 hover:bg-green-700 focus:ring-4 focus:ring-green-200 rounded-full flex items-center justify-center"
                  onClick={() => setReportFormOpen(true)}
                >
                  <FontAwesomeIcon icon={faFilePdfIcon} className="mr-2" />
                  Generate Report
                </button>
                <div className="mt-4">
                  <button className="inline-block px-8 py-3 text-sm text-center text-white font-bold bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 rounded-full">Create New Chart</button>
                  <select className={`px-4 py-2.5 ml-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 
                    ${darkMode ? "bg-gray-900 text-gray-200 border-gray-700" : "bg-white text-gray-800 border-gray-300"}`}>
                    <option>Chart Type</option>
                    <option>Bar Chart</option>
                    <option>Line Chart</option>
                    <option>Pie Chart</option>
                    <option>Area Chart</option>
                  </select>
                </div>
              </div>
            </main>
          </div>
        </div>
        {/* Floating AI Assistant */}
        <div className="fixed bottom-6 right-6 z-50">
          <button
            className="flex items-center px-4 py-3 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg transition-all hover:shadow-xl"
            onClick={() => setAiOpen(true)}
          >
            <FontAwesomeIcon icon={faRobot} className="text-white mr-2" />
            <span className="text-white font-medium">Ask AI</span>
          </button>
          {aiOpen && (
            <div
              ref={aiContainerRef}
              className="absolute bottom-16 right-0 w-80 max-w-sm bg-white dark:bg-gray-900 rounded-lg shadow-xl flex flex-col"
              style={{ height: 420 }}
            >
              {/* Chat Header */}
              <div className="flex justify-between items-center border-b pb-2 pt-3 px-4 bg-gray-50 dark:bg-gray-800 rounded-t-lg">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faRobot} className="w-7 h-7 text-blue-500 mr-2" />
                  <h3 className="font-bold text-gray-800 dark:text-gray-100">AI Assistant</h3>
                </div>
                <button
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => setAiOpen(false)}
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xl" />
                </button>
              </div>
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto mb-2 px-3 pt-2 pb-1 bg-gray-50 dark:bg-gray-900 rounded-b-lg">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex mb-2 ${msg.fromUser ? "justify-end" : ""}`}>
                    {!msg.fromUser && (
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white flex-shrink-0 mr-2">
                        <FontAwesomeIcon icon={faRobot} className="text-xs" />
                      </div>
                    )}
                    <div className={`rounded-lg p-2 max-w-[75%] text-sm ${msg.fromUser ? "bg-blue-600 text-white" : "bg-blue-100 dark:bg-gray-800 dark:text-gray-100 text-gray-800"}`}>
                      <div className="whitespace-pre-wrap">{msg.text}</div>
                    </div>
                    {msg.fromUser && (
                      <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-blue-500 flex-shrink-0 ml-2">
                        <FontAwesomeIcon icon={faUserCircle} className="text-xl" />
                      </div>
                    )}
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex mb-2">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white flex-shrink-0 mr-2">
                       <FontAwesomeIcon icon={faRobot} className="text-xs" />
                    </div>
                    <div className="rounded-lg p-2 max-w-[75%] text-sm bg-blue-100 dark:bg-gray-800 dark:text-gray-100 text-gray-800 animate-pulse">
                      Thinking...
                    </div>
                   </div>
                  )}
              </div>
            {/* Chat Input */}
            <form
              className="flex p-3 border-t border-gray-200 dark:border-gray-700"
              onSubmit={handleAiSend}
              encType="multipart/form-data"
            >
            {/* Paperclip button to trigger file picker */}
            <button
              type="button"
              className="mr-2 px-2 py-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              title="Attach files"
              onClick={() => fileInputRef.current.click()}
            >
              <span role="img" aria-label="Attach">📎</span>
            </button>
            {/* Hidden file input */}
            <input
              type="file"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleAiFileChange}
            />
            <input
              type="text"
              value={aiInput}
              onChange={e => setAiInput(e.target.value)}
              placeholder="Ask a question..."
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
            />
          <button
            type="submit"
            className="px-3 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600 flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </form>
          {/* Show selected files (optional, place directly below form) */}
          {aiFiles.length > 0 && (
            <div className="text-xs text-gray-500 mt-1 ml-3 flex items-center">
              <span>Files: {aiFiles.map(f => f.name).join(", ")}</span>
              <button
                className="ml-2 text-blue-500"
                onClick={clearAiFiles}
                type="button"
              >
            Clear
              </button>
            </div>
          )}      
            </div>
          )}
        </div>
        {/* Generate Report Modal */}
        {reportFormOpen && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-lg shadow-lg p-8 relative">
              {/* Close */}
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                onClick={() => setReportFormOpen(false)}
                aria-label="Close"
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
              <h2 className="text-xl font-bold mb-6 flex items-center text-gray-800 dark:text-gray-100">
                <FontAwesomeIcon icon={faFilePdfIcon} className="mr-2" />
                Generate Report
              </h2>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  setReportFormOpen(false);
                  alert("Report generated! (Wire up to real logic)");
                }}
                className="space-y-5"
              >
                {/* Report Type */}
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Report Type</label>
                  <select
                    className="w-full border px-3 py-2 rounded focus:ring focus:ring-blue-200 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-700"
                    value={reportType}
                    onChange={e => setReportType(e.target.value)}
                  >
                    <option value="Summary">Summary</option>
                    <option value="Detailed">Detailed</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>
                {/* Uploaded Files (Multi-select) */}
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Include Uploaded Forms</label>
                  <select
                    className="w-full border px-3 py-2 rounded focus:ring focus:ring-blue-200 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-700"
                    multiple
                    size={Math.min(4, uploadedFiles.length)}
                    value={selectedFiles}
                    onChange={e =>
                      setSelectedFiles(Array.from(e.target.selectedOptions, option => option.value))
                    }
                  >
                    {uploadedFiles.map((file, i) => (
                      <option key={i} value={file.name}>
                        {file.name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Tax Year */}
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Tax Year</label>
                  <select
                    className="w-full border px-3 py-2 rounded focus:ring focus:ring-blue-200 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-700"
                    value={selectedYear}
                    onChange={e => setSelectedYear(e.target.value)}
                  >
                    {taxYears.map((year, i) => (
                      <option key={i} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="w-full mt-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full"
                >
                  Generate Report
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}